import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

function Step2({ formData, onInputChange, onPreview, previewLoading, onThemesLoaded, disabled = false }) {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedThumbnails, setFailedThumbnails] = useState({});
  const listRef = useRef(null);

  // Desktop only: never cut a theme in half at the bottom of the list.
  // The list fills the space left by the other cards (so it lines up with Step 1's last field);
  // we count how many whole themes fit (with the normal gap between them) and spread the
  // leftover space as extra top/bottom padding inside every theme, so all themes look the same.
  const fitThemeList = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    list.style.removeProperty('--theme-pad-extra');
    if (window.innerWidth < 992) return;

    const items = Array.from(list.children);
    if (items.length < 2) return;
    const available = list.clientHeight;
    const gap = parseFloat(getComputedStyle(items[0]).marginBottom);

    // Each theme may also shrink by up to MAX_SHRINK px of padding top and bottom,
    // so one that only just doesn't fit still gets in
    const MAX_SHRINK = 4;
    let fitCount = 0;
    let itemsHeight = 0;
    for (const item of items) {
      const h = item.getBoundingClientRect().height;
      const count = fitCount + 1;
      if (itemsHeight + h + fitCount * gap - count * 2 * MAX_SHRINK > available) break;
      itemsHeight += h;
      fitCount = count;
    }
    if (fitCount === 0 || fitCount === items.length) return;

    const leftover = available - itemsHeight - (fitCount - 1) * gap;
    list.style.setProperty('--theme-pad-extra', `${leftover / fitCount / 2}px`);
  }, []);

  useLayoutEffect(() => {
    fitThemeList();
    window.addEventListener('resize', fitThemeList);
    return () => window.removeEventListener('resize', fitThemeList);
  }, [fitThemeList, themes]);

  // S3 base URL - Only place you need to update
  const S3_BASE_URL = process.env.REACT_APP_TEMPLATES_S3_BASE_URL;

  useEffect(() => {
    fetchAllThemes();
  }, []);

  const fetchAllThemes = async () => {
    try {
      setLoading(true);
      
      // Step 1: Fetch master index to get list of template folders
      const indexUrl = `${S3_BASE_URL}/index.json`;
      const indexResponse = await fetch(indexUrl);
      
      if (!indexResponse.ok) {
        throw new Error('Failed to fetch template index');
      }
      
      const indexData = await indexResponse.json();
      const templateFolders = indexData.templates || [];
      
      if (templateFolders.length === 0) {
        throw new Error('No templates found in index');
      }
      
      console.log(`Found ${templateFolders.length} templates:`, templateFolders);
      
      // Step 2: Fetch metadata for all templates in parallel
      const fetchPromises = templateFolders.map(folder => 
        fetchThemeMetadata(folder)
      );
      
      const results = await Promise.all(fetchPromises);
      
      // Step 3: Filter out any failed fetches
      const validThemes = results.filter(theme => theme !== null);
      
      if (validThemes.length === 0) {
        throw new Error('No valid themes could be loaded');
      }
      
      // Step 4: Assign sequential numbers (1, 2, 3, ...) based on array order
      const themesWithNumbers = validThemes.map((theme, index) => ({
        ...theme,
        number: index + 1  // Override with sequential number
      }));
      
      setThemes(themesWithNumbers);
      
      // Pass themes to parent component
      if (onThemesLoaded) {
        onThemesLoaded(themesWithNumbers);
      }
      
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching themes:', err);
      setError('Failed to load themes from S3. Please check your bucket configuration.');
      setLoading(false);
    }
  };

  const fetchThemeMetadata = async (folderName) => {
    try {
      const metadataUrl = `${S3_BASE_URL}/${folderName}/metadata.json`;
      const response = await fetch(metadataUrl);
      
      if (!response.ok) {
        console.warn(`Failed to fetch ${folderName}/metadata.json`);
        return null;
      }
      
      const metadata = await response.json();
      
      // Add preview_url dynamically based on folder structure
      return {
        ...metadata,
        preview_url: `${S3_BASE_URL}/${folderName}/index.html`,
        folder: folderName  // Store folder name for reference
      };
    } catch (err) {
      console.error(`Error fetching ${folderName}:`, err);
      return null;
    }
  };

  const handlePreviewClick = () => {
    if (disabled) {
      alert('Trial limit reached. Cannot preview websites.');
      return;
    }

    // Check if theme is selected
    if (!formData.selectedTheme) {
      alert('Please select a theme first!');
      return;
    }

    // Check if Step 1 data exists in localStorage
    const savedData = localStorage.getItem('websiteFormData');
    if (!savedData) {
      alert('Please fill and submit Step 1 form first!');
      return;
    }

    try {
      const step1Data = JSON.parse(savedData);
      
      // Validate Step 1 data
      if (!step1Data.websiteName || !step1Data.aboutUs || !step1Data.mainContent || 
          !step1Data.links || !step1Data.contactUs) {
        alert('Step 1 form data is incomplete. Please fill all fields in Step 1.');
        return;
      }

      console.log('Preview button clicked with:');
      console.log('- Step 1 Data:', step1Data);
      console.log('- Selected Theme:', formData.selectedTheme);

      // Call parent's preview handler
      onPreview();
      
    } catch (error) {
      console.error('Error reading localStorage:', error);
      alert('Error reading form data. Please fill Step 1 again.');
    }
  };

  const handleThemeSelect = (themeId) => {
    if (disabled) {
      return; // Prevent selection when disabled
    }
    onInputChange('selectedTheme', themeId);
  };

  const handleCheckboxChange = (field, value) => {
    if (disabled) {
      return; // Prevent changes when disabled
    }
    onInputChange(field, value);
  };

  if (loading) {
    return (
      <div className="step-card step-2">
        <div className="step-header-inline">
          <span className="step-number blue">2</span>
          <div>
            <h2 className="step-heading">Select your website theme</h2>
            <p className="step-subtitle">Choose a design that fits your business.</p>
          </div>
        </div>
        <div className="step-body">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner-large"></div>
            <p style={{ marginTop: '20px', color: '#666' }}>Loading themes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="step-card step-2">
        <div className="step-header-inline">
          <span className="step-number blue">2</span>
          <div>
            <h2 className="step-heading">Select your website theme</h2>
            <p className="step-subtitle">Choose a design that fits your business.</p>
          </div>
        </div>
        <div className="step-body">
          <div className="alert-warning" style={{ 
            padding: '20px', 
            background: '#fff3cd', 
            borderRadius: '6px',
            border: '1px solid #ffc107',
            color: '#856404',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
            <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>{error}</div>
            <div style={{ fontSize: '12px', marginTop: '15px' }}>
              <strong>Troubleshooting:</strong>
              <ul style={{ textAlign: 'left', marginTop: '10px', paddingLeft: '20px' }}>
                <li>Verify S3 bucket exists and is accessible</li>
                <li>Check if index.json is present in bucket root</li>
                <li>Ensure CORS is configured correctly</li>
                <li>Verify all files have public read permissions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="step-card step-2">
      <div className="step-header-inline">
        <span className="step-number blue">2</span>
        <div>
          <h2 className="step-heading">Select your website theme</h2>
          <p className="step-subtitle">Choose a design that fits your business.</p>
        </div>
      </div>
      
      <div className="step-body">
        <div className="theme-list-wrapper">
          <label className="form-label">Choose Visual Theme</label>

          <div className="theme-list" ref={listRef}>
            {themes.map((theme) => (
              <div
                key={theme.id}
                className={`theme-option ${formData.selectedTheme === theme.id ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => handleThemeSelect(theme.id)}
                style={disabled ? { 
                  opacity: 0.6, 
                  cursor: 'not-allowed',
                  pointerEvents: 'none' 
                } : {}}
              >
                <div className="theme-header">
                  {/* Thumbnail from metadata.json; falls back to the number if missing or it fails to load */}
                  {theme.thumbnail && !failedThumbnails[theme.id] ? (
                    <img
                      className="theme-thumbnail"
                      src={theme.thumbnail}
                      alt={theme.name}
                      loading="lazy"
                      onError={() => setFailedThumbnails(prev => ({ ...prev, [theme.id]: true }))}
                    />
                  ) : (
                    <div className="theme-number">{theme.number}</div>
                  )}
                  <div className="theme-info">
                    <div className="theme-name">
                      {/* Icon hidden in the list (still in metadata.json); uncomment to show it again */}
                      {/* <span>{theme.icon}</span> */}
                      <span>{theme.name}</span>
                    </div>
                    <div className="theme-description">{theme.description}</div>
                  </div>
                  <span
                    className={`theme-radio ${formData.selectedTheme === theme.id ? 'checked' : ''}`}
                    role="radio"
                    aria-checked={formData.selectedTheme === theme.id}
                    aria-label={theme.name}
                  ></span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="darkMode"
              checked={formData.darkMode}
              onChange={(e) => handleCheckboxChange('darkMode', e.target.checked)}
              // disabled={disabled}
              disabled={true} //for now
            />
            <label 
              className="form-check-label" 
              htmlFor="darkMode"
              style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              Include dark mode toggle
            </label>
          </div>

          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="customColor"
              checked={formData.customColor}
              onChange={(e) => handleCheckboxChange('customColor', e.target.checked)}
              // disabled={disabled}
              disabled={true} //for now

            />
            <label 
              className="form-check-label" 
              htmlFor="customColor"
              style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              Add custom color scheme
            </label>
          </div>

          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="seoListing"
              checked={formData.seoListing}
              onChange={(e) => handleCheckboxChange('seoListing', e.target.checked)}
              // disabled={disabled}
              disabled={true} //for now
            />
            <label 
              className="form-check-label" 
              htmlFor="seoListing"
              style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              List Site Upon Searching - SEO
            </label>
          </div>
        </div> */}

        <button 
          className="btn-custom btn-preview" 
          onClick={handlePreviewClick}
          disabled={false}
        >
          {previewLoading ? (
            <>
              <span className="spinner-small"></span>
              Generating...
            </>
          ) : (
            <>
              <span className="icon">👁</span>
              Preview
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default Step2;